import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export type UploadPhase = 'idle' | 'starting' | 'uploading' | 'validating' | 'completed' | 'failed';

export type ValidationResult = {
  isValid: boolean;
  issues: string[];
  lineCount?: number;
  fileRows?: number;
  missingColumns?: string[];
};

export interface UploadState {
  progress: number;
  phase: UploadPhase;
  validation?: ValidationResult;
  error?: string;
  errorDetails?: string[];
}

export interface UploadsSliceState {
  [experimentId: string]: UploadState;
}

const initialState: UploadsSliceState = {};

const uploadsSlice = createSlice({
  name: 'uploads',
  initialState,
  reducers: {
    uploadStarted(state, action: PayloadAction<{ experimentId: string }>) {
      const { experimentId } = action.payload;
      state[experimentId] = {
        progress: 0,
        phase: 'starting',
        validation: undefined,
        error: undefined,
        errorDetails: undefined,
      };
    },
    uploadProgress(state, action: PayloadAction<{ experimentId: string; progress: number }>) {
      const { experimentId, progress } = action.payload;
      if (state[experimentId]) {
        state[experimentId].progress = progress;
        state[experimentId].phase = 'uploading';
      }
    },
    uploadStepChanged(state, action: PayloadAction<{ experimentId: string; phase: UploadPhase }>) {
      const { experimentId, phase } = action.payload;
      if (state[experimentId]) {
        state[experimentId].phase = phase;
      }
    },
    uploadValidationResult(state, action: PayloadAction<{ experimentId: string; validation: ValidationResult }>) {
      const { experimentId, validation } = action.payload;
      if (state[experimentId]) {
        state[experimentId].validation = validation;
        state[experimentId].phase = 'completed';
        if (!validation.isValid) {
          state[experimentId].error = validation.issues?.join('\n');
        }
      }
    },
    uploadCompleted(state, action: PayloadAction<{ experimentId: string }>) {
      const { experimentId } = action.payload;
      if (state[experimentId]) {
        state[experimentId].phase = 'completed';
        state[experimentId].progress = 100;
        state[experimentId].error = undefined;
        state[experimentId].errorDetails = undefined;
      }
    },
    uploadFailed(state, action: PayloadAction<{ experimentId: string; error: string; errorDetails?: string[] }>) {
      const { experimentId, error, errorDetails } = action.payload;
      if (state[experimentId]) {
        state[experimentId].phase = 'failed';
        state[experimentId].error = error;
        state[experimentId].errorDetails = errorDetails;
      }
    },
    uploadCleared(state, action: PayloadAction<{ experimentId: string }>) {
      const { experimentId } = action.payload;
      if (state[experimentId]) {
        delete state[experimentId];
      }
    },
  },
});

export const { uploadStarted, uploadProgress, uploadStepChanged, uploadValidationResult, uploadCompleted, uploadFailed, uploadCleared } = uploadsSlice.actions;

export default uploadsSlice.reducer;
